/* eslint-disable no-nested-ternary */

import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import Button from '../../common/Button';
import displayInYen from '../../../core/displayInYen';
import { stickyBarTestIds as testIds } from './testIds';
import dispatchGtmAppEvent from '../../../core/googleTagManagerEvents/googleTagManagerEvents';
import { StartPrices } from '../../../../types/StartPrices';
import Lottie from '../../common/Lottie/Lottie';
import Loading from '../../../../public/animations/loading.json';
import { useSelector } from '../../../core/redux/store';

type Props = {
  hotelId: string | undefined;
  hotelName: string | undefined;
  bookingEngineExternalLink: string | undefined;
  startPrices?: StartPrices;
  isStartPricesLoading: boolean;
};

const StickyBar = ({
  hotelId,
  hotelName,
  bookingEngineExternalLink,
  startPrices,
  isStartPricesLoading,
}: Props) => {
  const { t: tCommon } = useTranslation('common');
  const { t: tHotel } = useTranslation('hotel');
  const router = useRouter();
  const { userAttributes } = useSelector((x) => x.account);
  const isLoggedIn = Boolean(userAttributes?.accountId);
  const startPrice = startPrices?.startPrice;
  const discountedStartPrice =
    isLoggedIn && startPrices?.membersDiscountedStartPrice
      ? startPrices?.membersDiscountedStartPrice
      : startPrices?.discountedStartPrice;

  const handleButtonClick = () => {
    if (hotelId) {
      const value = startPrice ? Number(startPrice) : null;
      dispatchGtmAppEvent({
        appEventId: 'booking_button_clicked',
        gaEventName: 'booking_start',
        gaCategory: 'booking_start',
        gaAction: 'click',
        gaLabel: hotelId,
        gaValue: value,
        hotelId,
        transactionValue: value,
      });
    }
  };

  return (
    <>
      <div
        className="fixed bottom-0 z-10 hidden w-full sm:block"
        style={{ background: 'rgba(19, 19, 30, 0.5)' }}
      >
        <div className="mx-auto flex max-w-screen-xl gap-2 py-6 sm:px-8 sm-max:px-6">
          <div className="flex flex-2 rounded bg-white">
            <div
              data-testid={testIds.hotel_name_desktop}
              className="flex flex-2 items-center px-10 py-4 text-regular"
            >
              {hotelName}
            </div>
            {isStartPricesLoading ? (
              <div className="h-14">
                <Lottie
                  style={{ width: 'auto', height: '100%' }}
                  params={{
                    loop: true,
                    animationData: Loading,
                  }}
                />
              </div>
            ) : startPrice ? (
              discountedStartPrice ? (
                <div className="flex flex-row items-center border-l-2 border-gray-200 px-10">
                  <p className="pr-3 text-mini" style={{ wordBreak: 'keep-all' }}>
                    {startPrice ? tHotel('sticky_bar.price_for_adult_one_night') : <br />}
                  </p>
                  <div className="flex flex-col items-end">
                    <p
                      data-testid={testIds.hotel_cheapest_price_desktop}
                      className="whitespace-nowrap text-mini text-orange-700 line-through"
                      style={{ wordBreak: 'keep-all' }}
                    >
                      {startPrice ? (
                        <>{displayInYen(Number(startPrice), router.locale)} ~</>
                      ) : (
                        <br />
                      )}
                    </p>
                    <p
                      data-testid={testIds.hotel_discounted_price_desktop}
                      className="whitespace-nowrap text-medium font-bold xl:text-title3"
                      style={{ wordBreak: 'keep-all' }}
                    >
                      {startPrice ? (
                        <>{displayInYen(discountedStartPrice, router.locale)} ~</>
                      ) : (
                        <br />
                      )}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex flex-row items-center border-l-2 border-gray-200 px-10">
                  <p className="pr-3 text-mini" style={{ wordBreak: 'keep-all' }}>
                    {startPrice ? tHotel('sticky_bar.price_for_adult_one_night') : <br />}
                  </p>
                  <p
                    data-testid={testIds.hotel_cheapest_price_desktop}
                    className="whitespace-nowrap text-medium font-bold xl:text-title3"
                    style={{ wordBreak: 'keep-all' }}
                  >
                    {startPrice ? <>{displayInYen(Number(startPrice), router.locale)} ~</> : <br />}
                  </p>
                </div>
              )
            ) : null}
          </div>
          <div className="flex-1">
            <Button
              data-testid={testIds.reservation_button_desktop}
              className="hotel-booking-button-sticky size-full px-0"
              to={bookingEngineExternalLink}
              color="red"
              onClick={handleButtonClick}
            >
              {tCommon('button.plan_details_and_book')}
            </Button>
          </div>
        </div>
      </div>

      {/* mobile view */}
      <div className="fixed bottom-0 z-10 block w-full bg-white shadow-3xl sm:hidden">
        <div className="mx-auto flex max-w-screen-xl gap-6 py-4 sm:px-8 sm-max:px-6">
          <div className="flex flex-1 flex-col">
            <p data-testid={testIds.hotel_name_mobile} className="pr-3 text-mini">
              {hotelName}
            </p>

            {isStartPricesLoading ? (
              <div className="h-14">
                <Lottie
                  style={{ width: 'auto', height: '100%' }}
                  params={{
                    loop: true,
                    animationData: Loading,
                  }}
                />
              </div>
            ) : startPrice ? (
              discountedStartPrice ? (
                <div className="flex flex-row items-start">
                  <p
                    data-testid={testIds.hotel_cheapest_price_mobile}
                    className="text-mini text-orange-700 line-through"
                  >
                    {startPrice ? <>{displayInYen(Number(startPrice), router.locale)} ~</> : <br />}
                  </p>
                  <p
                    data-testid={testIds.hotel_discounted_price_mobile}
                    className="text-large font-bold"
                  >
                    {startPrice ? (
                      <>{displayInYen(discountedStartPrice, router.locale)} ~</>
                    ) : (
                      <br />
                    )}
                  </p>
                </div>
              ) : (
                <p
                  data-testid={testIds.hotel_cheapest_price_mobile}
                  className="text-large font-bold"
                >
                  {startPrice ? <>{displayInYen(Number(startPrice), router.locale)} ~</> : <br />}
                </p>
              )
            ) : null}
          </div>
          <div className="flex-1">
            <Button
              data-testid={testIds.reservation_button_mobile}
              className="hotel-booking-button-sticky-mobile size-full px-0"
              size="small"
              to={bookingEngineExternalLink}
              color="red"
              onClick={handleButtonClick}
            >
              {tCommon('button.details_and_book')}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default StickyBar;
